import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * Tenants, as the platform console sees them — every edition, across the whole platform.
 *
 * Platform staff only (`isPlatformStaff`); the server's `requirePlatformScope` is the enforcement.
 */

export const ACCESS_LEVELS = ['none', 'read_only', 'full'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

export const TENANT_STATUSES = ['active', 'suspended', 'archived'] as const
export type TenantStatus = (typeof TENANT_STATUSES)[number]

export const tenantSummarySchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  planKey: z.string(),
  planName: z.string(),
  eventSeriesSlug: z.string(),
  eventSeriesName: z.string(),
  /** Null for a tenant provisioned before the route bound a domain. See `isReachable`. */
  hostname: z.string().nullable(),
  defaultLocale: z.string(),
  timezone: z.string(),
  currencyCode: z.string(),
  createdAt: z.string(),
})

export type TenantSummary = z.infer<typeof tenantSummarySchema>

/** A new edition, with what became of its starter site: a failure there still created it. */
export const provisionedTenantSchema = tenantSummarySchema.extend({
  starterTemplate: z.object({
    status: z.enum(['applied', 'not_applicable', 'failed']),
    documents: z.number(),
    menus: z.number(),
    message: z.string().nullable(),
  }),
})

export type ProvisionedTenant = z.infer<typeof provisionedTenantSchema>

const tenantModuleSchema = z.object({
  key: z.string(),
  core: z.boolean(),
  requires: z.array(z.string()),
  soft: z.array(z.string()),
  accessLevel: z.enum(ACCESS_LEVELS),
  source: z.enum(['plan', 'override']),
  planLevel: z.enum(ACCESS_LEVELS),
  grantedAt: z.string().nullable(),
  grantedBy: z.object({ id: z.uuid(), email: z.string() }).nullable(),
  note: z.string().nullable(),
})

export type TenantModule = z.infer<typeof tenantModuleSchema>

export const tenantDetailSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(TENANT_STATUSES),
  timezone: z.string(),
  defaultLocale: z.string(),
  currencyCode: z.string(),
  currencyLocked: z.boolean(),
  createdAt: z.string(),
  plan: z.object({ id: z.uuid(), key: z.string(), name: z.string() }),
  eventSeries: z.object({ id: z.uuid(), slug: z.string(), name: z.string() }),
  domains: z.array(
    z.object({
      id: z.uuid(),
      hostname: z.string(),
      isPrimary: z.boolean(),
      verifiedAt: z.string().nullable(),
      createdAt: z.string(),
      boundBy: z.string().nullable(),
    }),
  ),
  modules: z.array(tenantModuleSchema),
})

export type TenantDetail = z.infer<typeof tenantDetailSchema>

export function fetchTenants(signal?: AbortSignal): Promise<{ items: TenantSummary[] }> {
  return api.get('/v1/platform/tenants', {
    schema: z.object({ items: z.array(tenantSummarySchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function fetchTenant(tenantId: string, signal?: AbortSignal): Promise<TenantDetail> {
  return api.get(`/v1/platform/tenants/${tenantId}`, {
    schema: tenantDetailSchema,
    ...(signal ? { signal } : {}),
  })
}

const provisionOptionsSchema = z.object({
  plans: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      name: z.string(),
      modules: z.record(z.string(), z.enum(ACCESS_LEVELS)),
    }),
  ),
  eventSeries: z.array(
    z.object({
      id: z.uuid(),
      slug: z.string(),
      name: z.string(),
      template: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    }),
  ),
})

export type ProvisionOptions = z.infer<typeof provisionOptionsSchema>

export function fetchProvisionOptions(signal?: AbortSignal): Promise<ProvisionOptions> {
  return api.get('/v1/platform/options', {
    schema: provisionOptionsSchema,
    ...(signal ? { signal } : {}),
  })
}

const checkSchema = z.object({
  ok: z.boolean(),
  code: z.string().optional(),
  message: z.string().optional(),
})

export type AvailabilityCheck = z.infer<typeof checkSchema>

/** The create route's own slug / hostname rules, asked as the operator types. */
export function checkAvailability(
  query: { slug?: string; hostname?: string },
  signal?: AbortSignal,
): Promise<{ slug?: AvailabilityCheck | undefined; hostname?: AvailabilityCheck | undefined }> {
  const params = new URLSearchParams()
  if (query.slug !== undefined) params.set('slug', query.slug)
  if (query.hostname !== undefined) params.set('hostname', query.hostname)
  return api.get(`/v1/platform/tenants/availability?${params.toString()}`, {
    schema: z.object({ slug: checkSchema.optional(), hostname: checkSchema.optional() }),
    ...(signal ? { signal } : {}),
  })
}

export type ProvisionInput = {
  slug: string
  name: string
  eventSeriesId: string
  planId: string
  hostname: string
  defaultLocale: string
  timezone: string
  currencyCode: string
}

export function provisionTenant(
  input: ProvisionInput & { fromTemplate?: CloneCopy },
): Promise<ProvisionedTenant> {
  return api.post('/v1/platform/tenants', { schema: provisionedTenantSchema, body: input })
}

export function changeModules(
  tenantId: string,
  input: { modules: Record<string, AccessLevel>; force: boolean; note?: string },
) {
  return api.put(`/v1/platform/tenants/${tenantId}/modules`, {
    schema: z.object({ modules: z.record(z.string(), z.enum(ACCESS_LEVELS)) }),
    body: input,
  })
}

export function resetModuleOverride(tenantId: string, moduleKey: string, force: boolean) {
  return api.delete(
    `/v1/platform/tenants/${tenantId}/modules/${moduleKey}/override?force=${String(force)}`,
    { schema: z.object({ moduleKey: z.string(), accessLevel: z.enum(ACCESS_LEVELS) }) },
  )
}

/**
 * Can anything actually reach this tenant?
 *
 * Tenant resolution is by `Host`. A tenant with no verified domain row has roles, modules and a
 * plan and is unreachable by every request — the state provisioning could leave behind until the
 * route started binding a domain. Surfaced in the list rather than left invisible, because the
 * tenants already in that state are the ones nobody will think to look for.
 */
export function isReachable(tenant: Pick<TenantSummary, 'hostname'>): boolean {
  return tenant.hostname !== null && tenant.hostname !== ''
}

export type SettingsInput = {
  name?: string
  timezone?: string
  defaultLocale?: string
  currencyCode?: string
}

export function updateTenantSettings(
  tenantId: string,
  input: SettingsInput,
): Promise<TenantDetail> {
  return api.patch(`/v1/platform/tenants/${tenantId}`, { schema: tenantDetailSchema, body: input })
}

export function changeTenantStatus(tenantId: string, status: TenantStatus, reason: string) {
  return api.post(`/v1/platform/tenants/${tenantId}/status`, {
    schema: z.object({ status: z.enum(TENANT_STATUSES) }),
    body: { status, reason },
  })
}

export function deleteTenant(tenantId: string, confirmSlug: string, reason: string) {
  return api.delete(`/v1/platform/tenants/${tenantId}`, {
    schema: z.object({ ok: z.literal(true), releasedHostnames: z.array(z.string()) }),
    body: { confirmSlug, reason },
  })
}

export function addDomain(tenantId: string, hostname: string, primary: boolean) {
  return api.post(`/v1/platform/tenants/${tenantId}/domains`, {
    schema: z.object({ id: z.uuid(), hostname: z.string() }),
    body: { hostname, primary },
  })
}

export function setPrimaryDomain(tenantId: string, domainId: string) {
  return api.post(`/v1/platform/tenants/${tenantId}/domains/${domainId}/primary`, {
    schema: z.object({ hostname: z.string() }),
  })
}

export function removeDomain(tenantId: string, domainId: string) {
  return api.delete(`/v1/platform/tenants/${tenantId}/domains/${domainId}`, {
    schema: z.object({ hostname: z.string() }),
  })
}

const ownerSchema = z.object({
  userId: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.string(),
  banned: z.boolean(),
  isStaff: z.boolean(),
})

export type TenantOwner = z.infer<typeof ownerSchema>

export function fetchOwners(tenantId: string, signal?: AbortSignal) {
  return api.get(`/v1/platform/tenants/${tenantId}/owners`, {
    schema: z.object({ items: z.array(ownerSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export type CloneCopy = {
  moduleOverrides: boolean
  theme: boolean
  organizer: boolean
  roles: boolean
  profileFields: boolean
}

export function cloneTenant(
  sourceId: string,
  input: ProvisionInput & { copy: CloneCopy },
): Promise<ProvisionedTenant> {
  return api.post(`/v1/platform/tenants/${sourceId}/clone`, {
    schema: provisionedTenantSchema,
    body: input,
  })
}
